import type { Project } from "../types";

export const projectCategories = [
  "water",
  "tunnels_rail",
  "bridges_parking",
  "buildings",
  "other",
] as const;

// Du plus précoce au plus tardif : plus un projet est en amont, plus il est utile.
export const projectPhases = [
  "Concept",
  "Feasibility study",
  "Design",
  "Tender",
  "Awarded",
  "Pre-construction",
  "Construction",
  "Completed",
] as const;

export const earlyPhases: string[] = ["Concept", "Feasibility study", "Design"];

export const phaseKey = (phase: string) =>
  `resources.projects.phases.${phase.toLowerCase().replace(/[^a-z]+/g, "_")}`;

// Du plus proche de la décision au plus éloigné, pour l'ordre d'affichage.
export const companyRoles = [
  "Client / Project Owner",
  "Developer",
  "Project management company",
  "Engineering company",
  "Design consultant",
  "Architect",
  "Waterproofing consultant",
  "Concrete consultant",
  "Main contractor",
  "General contractor",
  "EPC contractor",
  "Civil contractor",
  "Waterproofing contractor",
  "Local distributor or supplier",
  "Funder",
];

export const roleKey = (role: string) =>
  `resources.projects.roles.${role.toLowerCase().replace(/[^a-z]+/g, "_")}`;

export const categoryKey = (category: string) =>
  `resources.projects.categories.${category}`;

// Typologie des sources (`apps/worker/config/source_types.toml`), du plus en amont au moins officiel.
export const sourceTypes = [
  "programme",
  "owner",
  "permit",
  "funder",
  "decision",
  "tender",
  "press",
  "social",
  "web",
] as const;

export const sourceTypeKey = (type: string) =>
  `resources.projects.source_types.${type}`;

export const countryName = (
  code: string | null | undefined,
  locale: string,
) => {
  if (!code) return "";
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
};

export const formatBudget = (
  project: Pick<Project, "budget_amount" | "budget_currency">,
  locale: string,
) => {
  if (project.budget_amount == null) return "";
  const amount = new Intl.NumberFormat(locale, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(project.budget_amount);
  return `${amount} ${project.budget_currency ?? ""}`.trim();
};

/** Montant en euros au dernier cours, affiché comme une estimation (« ≈ 3,1 M€ »). */
export const formatEur = (value: number | null | undefined, locale: string) =>
  value == null
    ? ""
    : `≈ ${new Intl.NumberFormat(locale, {
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(value)} €`;

export const formatDay = (date: string | null | undefined, locale: string) =>
  date ? new Date(date).toLocaleDateString(locale) : "";

/** Date de début ou de fin telle que la source la donne : année seule, mois ou jour, sans inventer de précision
 * (« 2027 » n'est pas le 1er janvier 2027). Lue en UTC pour ne pas changer de jour selon le fuseau. */
export const formatPartialDate = (
  date: string | null | undefined,
  locale: string,
) => {
  if (!date) return "";
  const match = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?$/.exec(date);
  if (!match) return date;
  const [, year, month, day] = match;
  if (!month) return year;
  const value = new Date(Date.UTC(+year, +month - 1, day ? +day : 1));
  return value.toLocaleDateString(
    locale,
    day
      ? { timeZone: "UTC" }
      : { timeZone: "UTC", month: "2-digit", year: "numeric" },
  );
};

/** Filtre par défaut de la liste : la catégorie « autre » est masquée. */
export const HIDE_OTHER = { "category@neq": "other" };

/** Sous ce seuil, une valeur déduite par l'IA est signalée « à vérifier » (donnée ou place dans la chaîne). */
export const LOW_CONFIDENCE = 0.6;
